import { useEffect, useMemo, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import {
  formatTimeRu,
  toDateInputValue,
  WEEKDAYS,
} from "../../../utils/appointmentLabels";
import { Link } from "react-router-dom";
import { PATIENTS_BASE } from "../AdminPatients/patientConstants";
import StatusBadge from "../ui/StatusBadge";
import AdminDoctorBookSlot from "./AdminDoctorBookSlot";
import {
  filterAssignedBranches,
  pickDefaultBranchId,
  doctorBranchOptionsFromResponse,
} from "./doctorConstants";
import s from "../clinic.module.css";
import { Button, Field, Input, Select, useToast } from "../../ui";

function weekRange(baseDate) {
  const d = new Date(`${baseDate}T12:00:00`);
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  const days = [];
  for (let i = 0; i < 7; i++) {
    const x = new Date(monday);
    x.setDate(monday.getDate() + i);
    days.push(toDateInputValue(x));
  }
  return days;
}

function addDays(dateStr, n) {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + n);
  return toDateInputValue(d);
}

export default function AdminDoctorCalendar({ doctorId, doctorBranchOptions }) {
  const toast = useToast();
  const today = toDateInputValue();
  const [weekStart, setWeekStart] = useState(() => {
    const days = weekRange(today);
    return days[0];
  });
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [bookDay, setBookDay] = useState(null);

  const days = useMemo(() => weekRange(weekStart), [weekStart]);

  const reload = () => {
    if (!doctorId || !days.length) return;
    const params = { from: days[0], to: days[6] };
    if (branchId) params.branch_id = branchId;
    clinicAPI
      .getDoctorCalendar(doctorId, params)
      .then(setData)
      .catch((e) => {
        toast.error(e.response?.data?.error || "Не удалось загрузить календарь");
        setData({ appointments: [], overrides: [], weekly_templates: [] });
      });
  };

  useEffect(() => {
    const applyBranches = (options) => {
      const list = filterAssignedBranches(options);
      setBranches(list);
      setBranchId((prev) =>
        list.some((b) => b.id === prev) ? prev : pickDefaultBranchId(list),
      );
    };

    if (doctorBranchOptions?.length) {
      applyBranches(doctorBranchOptions);
      return;
    }
    if (!doctorId) return;

    clinicAPI
      .getDoctor(doctorId)
      .then((r) => applyBranches(doctorBranchOptionsFromResponse(r.doctor)))
      .catch(() => toast.error("Не удалось загрузить залы тренера"));
  }, [doctorId, doctorBranchOptions, toast]);

  useEffect(() => {
    reload();
  }, [doctorId, days, branchId]);

  const shiftWeek = (delta) => {
    setWeekStart((prev) => addDays(prev, delta));
  };

  const dayLabel = (iso) => {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString("ru-RU", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const apptsByDay = (iso) =>
    (data?.appointments || []).filter(
      (a) => new Date(a.start_datetime).toISOString().slice(0, 10) === iso,
    );

  const overridesByDay = (iso) =>
    (data?.overrides || []).filter((o) => o.date === iso);

  const weeklyLabel = (dow) =>
    WEEKDAYS.find((w) => w.id === dow)?.short || dow;

  const isClosed = (iso) =>
    overridesByDay(iso).some((o) => o.kind === "closed");

  return (
    <div>
      <div className={s.calendarToolbar}>
        <Field label="Зал">
          <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            <option value="">Все</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Неделя с">
          <Input
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(e.target.value)}
          />
        </Field>
      </div>

      <div className={s.doctorWeekNav}>
        <button
          type="button"
          className={s.doctorWeekNavBtn}
          onClick={() => shiftWeek(-7)}
          aria-label="Предыдущая неделя"
        >
          ‹
        </button>
        <span className={s.doctorWeekNavLabel}>
          {dayLabel(days[0])} — {dayLabel(days[6])}
        </span>
        <button
          type="button"
          className={s.doctorWeekNavBtn}
          onClick={() => shiftWeek(7)}
          aria-label="Следующая неделя"
        >
          ›
        </button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setWeekStart(weekRange(today)[0])}
        >
          Сегодня
        </Button>
      </div>

      {(data?.weekly_templates || []).length > 0 && (
        <p className={s.attachmentHint} style={{ marginBottom: 12 }}>
          Шаблон:{" "}
          {(data.weekly_templates || [])
            .map(
              (w) =>
                `${weeklyLabel(w.day_of_week)} ${String(w.start_time).slice(0, 5)}–${String(w.end_time).slice(0, 5)}`,
            )
            .join(" · ")}
        </p>
      )}

      <div className={s.doctorWeekGrid}>
        {days.map((iso) => {
          const count = apptsByDay(iso).length;
          const closed = isClosed(iso);
          return (
            <div
              key={iso}
              className={`${s.doctorWeekDay} ${closed ? s.doctorWeekDayClosed : ""}`}
            >
              <div className={s.doctorWeekDayHead}>
                <span className={s.doctorWeekDayLabel}>{dayLabel(iso)}</span>
                {count > 0 ? (
                  <span className={s.doctorWeekDayCount} title={`${count} записей`}>
                    {count}
                  </span>
                ) : null}
                {branchId && !closed ? (
                  <button
                    type="button"
                    className={s.calAddBtn}
                    onClick={() => setBookDay(iso)}
                    title="Новая запись"
                  >
                    +
                  </button>
                ) : null}
              </div>
              {overridesByDay(iso).map((o) => (
                <div
                  key={o.id}
                  className={
                    o.kind === "closed" ? s.calBlockClosed : s.calBlockExtra
                  }
                >
                  {o.kind === "closed"
                    ? "Выходной"
                    : `Доп. ${String(o.start_time).slice(0, 5)}–${String(o.end_time).slice(0, 5)}`}
                </div>
              ))}
              {apptsByDay(iso).map((a) => (
                <div key={a.id} className={s.calAppt}>
                  <div className={s.calApptTime}>
                    {formatTimeRu(a.start_datetime)}
                  </div>
                  {a.patient?.id ? (
                    <Link
                      to={`${PATIENTS_BASE}/${a.patient.id}`}
                      className={s.patientLink}
                    >
                      {a.patient.full_name}
                    </Link>
                  ) : (
                    <span>{a.patient?.full_name}</span>
                  )}
                  <StatusBadge status={a.status} />
                </div>
              ))}
              {!apptsByDay(iso).length && !overridesByDay(iso).length && (
                <span className={s.calEmpty}>—</span>
              )}
            </div>
          );
        })}
      </div>

      {branchId ? (
        <p className={s.calHint}>
          Кнопка «+» в заголовке дня — запись на свободный слот в выбранном
          зале. Кружок с числом — количество записей на день.
        </p>
      ) : (
        <p className={s.calHint}>
          Выберите зал, чтобы записать клиента на свободный слот.
        </p>
      )}

      <AdminDoctorBookSlot
        open={!!bookDay}
        onClose={() => setBookDay(null)}
        doctorId={doctorId}
        branchId={branchId}
        date={bookDay}
        onBooked={reload}
      />
    </div>
  );
}
